import React, { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { toastService } from '../utils/toast';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Gamepad2, 
  Coins,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  PieChart,
  LineChart,
  Activity,
  Target,
  Clock,
  Award
} from 'lucide-react';

interface AnalyticsData {
  users: {
    total: number;
    newThisWeek: number;
    newThisMonth: number;
    activeToday: number;
    growthRate: number;
  };
  games: {
    total: number;
    completedToday: number;
    completedThisWeek: number;
    completedThisMonth: number;
    averageDuration: number;
    popularLevels: Array<{level: number, count: number}>;
  };
  revenue: {
    totalRevenue: number;
    revenueThisMonth: number;
    averagePerUser: number;
    topPackages: Array<{package: string, sales: number, revenue: number}>;
  };
  engagement: {
    dailyActiveUsers: number;
    averageSessionTime: number;
    retentionRate: number;
    churnRate: number;
  };
}

const Analytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    users: {
      total: 0,
      newThisWeek: 0,
      newThisMonth: 0,
      activeToday: 0,
      growthRate: 0
    },
    games: {
      total: 0,
      completedToday: 0,
      completedThisWeek: 0,
      completedThisMonth: 0,
      averageDuration: 0,
      popularLevels: []
    },
    revenue: {
      totalRevenue: 0,
      revenueThisMonth: 0,
      averagePerUser: 0,
      topPackages: []
    },
    engagement: {
      dailyActiveUsers: 0,
      averageSessionTime: 0,
      retentionRate: 0,
      churnRate: 0
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    const loadingToast = toastService.loading('Adatok betöltése...');
    try {
      setLoading(true);
      
      // Mock adatok - valódi implementációban Firebase-ből töltenénk
      const mockData: AnalyticsData = {
        users: {
          total: 1247,
          newThisWeek: 89,
          newThisMonth: 342,
          activeToday: 156,
          growthRate: 12.5
        },
        games: {
          total: 8932,
          completedToday: 234,
          completedThisWeek: 1456,
          completedThisMonth: 5678,
          averageDuration: 8.5,
          popularLevels: [
            { level: 1, count: 3245 },
            { level: 2, count: 2876 },
            { level: 3, count: 1987 },
            { level: 4, count: 634 },
            { level: 5, count: 190 }
          ]
        },
        revenue: {
          totalRevenue: 45678.90,
          revenueThisMonth: 8943.25,
          averagePerUser: 36.65,
          topPackages: [
            { package: 'Népszerű Csomag', sales: 456, revenue: 4559.44 },
            { package: 'Pro Csomag', sales: 234, revenue: 4206.66 },
            { package: 'Kezdő Csomag', sales: 789, revenue: 2359.11 },
            { package: 'Prémium Csomag', sales: 123, revenue: 4916.77 }
          ]
        },
        engagement: {
          dailyActiveUsers: 456,
          averageSessionTime: 23.4,
          retentionRate: 68.5,
          churnRate: 5.2
        }
      };

      setAnalyticsData(mockData);
      setLoading(false);
      setIsInitialLoad(false);
      toastService.dismiss(loadingToast);
      
      // Nincs szükség success toast-ra normál betöltésnél
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Hiba történt az analitikai adatok betöltésekor');
      setLoading(false);
      setIsInitialLoad(false); // Reset flag hiba esetén is
      toastService.dismiss(loadingToast);
      // A setError már beállítja a hibaüzenetet a UI-ban, nem kell dupla toast
    }
  };

  const exportData = () => {
    try {
      const data = JSON.stringify(analyticsData, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toastService.success('Analitikai adatok sikeresen exportálva');
    } catch (error) {
      console.error('Export error:', error);
      toastService.error('Hiba történt az export során');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Analitika</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Részletes statisztikák és jelentések a Game Winners játékról
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="form-input w-full sm:w-auto"
          >
            <option value="week">Hét</option>
            <option value="month">Hónap</option>
            <option value="year">Év</option>
          </select>
          <button onClick={loadAnalyticsData} className="btn-secondary w-full sm:w-auto">
            <RefreshCw className="mr-2 h-4 w-4" />
            Frissítés
          </button>
          <button onClick={exportData} className="btn-primary w-full sm:w-auto">
            <Download className="mr-2 h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes Felhasználó</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analyticsData.users.total.toLocaleString()}</p>
                <div className="flex items-center text-sm">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-green-600 dark:text-green-400">+{analyticsData.users.growthRate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Gamepad2 className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Játékok Ma</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analyticsData.games.completedToday}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Átlag: {analyticsData.games.averageDuration} perc</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Coins className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Bevétel (hónap)</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">${analyticsData.revenue.revenueThisMonth.toLocaleString()}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Átlag/fő: ${analyticsData.revenue.averagePerUser}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Megtartás</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analyticsData.engagement.retentionRate}%</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Lemorzsolódás: {analyticsData.engagement.churnRate}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* User Growth */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Felhasználó Növekedés</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Új regisztrációk (hét)</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{analyticsData.users.newThisWeek}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Új regisztrációk (hónap)</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{analyticsData.users.newThisMonth}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Aktív ma</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{analyticsData.users.activeToday}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Növekedési ráta</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">+{analyticsData.users.growthRate}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Popular Game Levels */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Népszerű Szintek</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {analyticsData.games.popularLevels.map((level) => (
                <div key={level.level} className="flex items-center">
                  <div className="flex-shrink-0 w-16">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Szint {level.level}</span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(level.count / analyticsData.games.popularLevels[0].count) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{level.count.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bevétel Részletezés</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {analyticsData.revenue.topPackages.map((pkg) => (
                <div key={pkg.package} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{pkg.package}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{pkg.sales} eladás</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">${pkg.revenue.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Engagement Metrikák</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-xl font-bold text-blue-900 dark:text-blue-100">{analyticsData.engagement.dailyActiveUsers}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Napi aktív</p>
              </div>
              
              <div className="text-center p-3 bg-green-50 dark:bg-green-900 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="h-5 w-5 text-green-500" />
                </div>
                <p className="text-xl font-bold text-green-900 dark:text-green-100">{analyticsData.engagement.averageSessionTime}m</p>
                <p className="text-xs text-green-600 dark:text-green-400">Átlag munkamenet</p>
              </div>
              
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Award className="h-5 w-5 text-purple-500" />
                </div>
                <p className="text-xl font-bold text-purple-900 dark:text-purple-100">{analyticsData.engagement.retentionRate}%</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">Megtartás</p>
              </div>
              
              <div className="text-center p-3 bg-red-50 dark:bg-red-900 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <TrendingDown className="h-5 w-5 text-red-500" />
                </div>
                <p className="text-xl font-bold text-red-900 dark:text-red-100">{analyticsData.engagement.churnRate}%</p>
                <p className="text-xs text-red-600 dark:text-red-400">Lemorzsolódás</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Összesítés</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Teljesítmény</h4>
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                A felhasználók száma {analyticsData.users.growthRate}%-kal nőtt, 
                ami jó növekedési trendet mutat.
              </p>
            </div>
            
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Bevétel</h4>
              <div className="flex items-center justify-center mb-2">
                <Coins className="h-8 w-8 text-yellow-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                A havi bevétel stabil, az átlagos felhasználónkénti bevétel 
                ${analyticsData.revenue.averagePerUser}.
              </p>
            </div>
            
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Engagement</h4>
              <div className="flex items-center justify-center mb-2">
                <Activity className="h-8 w-8 text-purple-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                A {analyticsData.engagement.retentionRate}%-os megtartási ráta 
                kiváló felhasználói elégedettséget jelez.
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Hiba
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics; 